import React, { useState, useEffect } from 'react';
import { Phone, Mail, Clock, Instagram, Facebook, X, Send, Loader, Sparkles, MapPin, MessageCircle } from 'lucide-react';
import { API_BASE } from '../config';

export default function Contact({ t }) {
  const [info, setInfo] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/settings`)
      .then(res => res.json())
      .then(data => setInfo(data))
      .catch(err => console.error("Could not load contact info", err));
  }, []);

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    setSendStatus(null);
    
    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailForm)
      });
      
      if (res.ok) {
        setSendStatus('success');
        setEmailForm({ name: '', email: '', message: '' });
        setTimeout(() => {
          setShowEmailModal(false);
          setSendStatus(null);
        }, 2000);
      } else {
        setSendStatus('error');
      }
    } catch (err) {
      console.error("Error sending email", err);
      setSendStatus('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
      {/* En-tête élégant */}
      <div className="relative py-12 px-4 bg-gradient-to-br from-slate-900 via-brand-900 to-slate-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAzLTRzMyAyIDMgNHYyYzAgMi0yIDQtMyA0cy0zLTItMy00di0yem0wLTMwYzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNFY0ek0wIDM0YzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNHYtMnptMC0zMGMwLTIgMi00IDMtNHMzIDIgMyA0djJjMCAyLTIgNC0zIDRzLTMtMi0zLTRWNHoiLz48L2c+PC9nPjwvc3ZnPg==')] animate-pulse"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center p-1 mb-4 bg-white/10 backdrop-blur-xl rounded-full border border-white/30 px-4">
            <Sparkles className="h-3 w-3 mr-2 text-brand-300" />
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-brand-50">
              {t.contact?.badge || 'Restons en Contact'}
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4 font-serif italic tracking-tight">
            {t.contact?.title || 'Contactez-nous'}
          </h2>
          <p className="text-base text-slate-200 font-medium opacity-90">
            {t.contact?.subtitle || 'Nous sommes à votre écoute pour répondre à toutes vos questions'}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Cartes de contact principales */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white rounded-3xl shadow-lg p-8 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-200 group">
            <div className="w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
              <Phone className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-3 font-serif italic">
              {t.contact?.phone || 'Téléphone'}
            </h3>
            <a 
              href={`tel:${info?.phone || '+33123456789'}`}
              className="text-lg text-brand-600 font-bold hover:text-brand-700 transition-colors"
            >
              {info?.phone || "+33 1 23 45 67 89"}
            </a>
          </div>
          
          <div 
            onClick={() => setShowEmailModal(true)} 
            className="bg-white rounded-3xl shadow-lg p-8 text-center cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-200 group"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
              <Mail className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-3 font-serif italic">
              {t.contact?.email || 'Email'}
            </h3>
            <p className="text-lg text-brand-600 font-bold group-hover:text-brand-700 transition-colors">
              {info?.email || "contact@Haircare.fr"}
            </p>
            <p className="text-sm text-slate-500 mt-2"> 
			  {t.contact?.emailSend || 'Cliquez pour envoyer un message'}
			  </p>
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-8 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-200 group">
            <div className="w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
              <Clock className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-3 font-serif italic">
              {t.contact?.hours || 'Horaires'}
            </h3>
            <p className="text-slate-600 font-medium leading-relaxed">
              {info?.hours || t.contact?.hoursText || "Lun-Dim: 10h-20h"}
            </p>
          </div>
        </div>

        {/* Section réseaux sociaux */}
        {(info?.instagram || info?.facebook) && (
          <div className="bg-white rounded-3xl shadow-xl p-10 border border-slate-200">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-0.5 bg-brand-600"></div>
                <h3 className="text-3xl font-black text-slate-900 font-serif italic">
                  {t.contact?.socialTitle || 'Suivez-nous'}
                </h3>
                <div className="w-12 h-0.5 bg-brand-600"></div>
              </div>
              <p className="text-slate-600 font-medium">
                {t.contact?.socialText || 'Rejoignez notre communauté sur les réseaux sociaux'}
              </p>
            </div>
            
            <div className="flex justify-center gap-6">
              {info.instagram && (
                <a 
                  href={info.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="group"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:scale-110">
                    <Instagram className="h-10 w-10 text-white" />
                  </div>
                  <p className="text-center mt-3 text-sm font-bold text-slate-700 group-hover:text-brand-600 transition-colors">
                    Instagram
                  </p>
                </a>
              )}
              {info.facebook && (
                <a 
                  href={info.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:scale-110">
                    <Facebook className="h-10 w-10 text-white" />
                  </div>
                  <p className="text-center mt-3 text-sm font-bold text-slate-700 group-hover:text-brand-600 transition-colors">
                    Facebook
                  </p>
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal Email améliorée */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg relative overflow-hidden animate-scale-in">
            <button 
              onClick={() => setShowEmailModal(false)} 
              className="absolute top-6 right-6 w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 transition-all z-10 hover:rotate-90"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="bg-gradient-to-br from-brand-600 to-brand-800 px-8 py-6 relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <MessageCircle className="h-32 w-32 text-white absolute -right-8 -top-8" />
              </div>
              <h3 className="text-2xl font-black text-white flex items-center relative z-10">
                <Mail className="h-6 w-6 mr-3" />
                {t.contact?.email || 'Envoyez-nous un message'}
              </h3>
            </div>
            
            <div className="p-8">
              {sendStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send className="h-10 w-10 text-green-600" />
                  </div>
                  <p className="text-2xl font-black text-green-600 mb-2">Message envoyé !</p>
                  <p className="text-slate-600">Nous vous répondrons très bientôt</p>
                </div>
              ) : (
                <form onSubmit={handleSendEmail} className="space-y-6">
                  {sendStatus === 'error' && (
                    <div className="p-4 bg-red-50 text-red-700 rounded-2xl text-sm flex items-center border border-red-200">
                      <X className="h-5 w-5 mr-3 flex-shrink-0" />
                      {t.booking?.error || 'Une erreur est survenue'}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">
                      {t.auth?.name || 'Nom'}
                    </label>
                    <input 
                      type="text" 
                      required 
                      value={emailForm.name} 
                      onChange={(e) => setEmailForm({...emailForm, name: e.target.value})} 
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all font-medium"
                      placeholder="Votre nom complet"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">
                      {t.auth?.email || 'Email'}
                    </label>
                    <input 
                      type="email" 
                      required 
                      value={emailForm.email} 
                      onChange={(e) => setEmailForm({...emailForm, email: e.target.value})} 
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all font-medium"
                      placeholder="votre@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-2">
                      Message
                    </label>
                    <textarea 
                      required 
                      rows="5" 
                      value={emailForm.message} 
                      onChange={(e) => setEmailForm({...emailForm, message: e.target.value})} 
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all font-medium resize-none"
                      placeholder="Votre message..."
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={sending} 
                    className="w-full group inline-flex items-center justify-center bg-brand-600 text-white px-10 py-4 rounded-full font-black text-lg hover:bg-brand-700 transition-all shadow-xl transform hover:-translate-y-1 hover:shadow-brand-500/40 border-b-4 border-brand-800 active:translate-y-0 active:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? (
                      <>
                        <Loader className="animate-spin h-5 w-5 mr-3" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5 mr-3 group-hover:translate-x-1 transition-transform" />
                        {t.auth?.submitLogin || 'Envoyer le message'}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}